import React, { useState, useEffect } from 'react';
import { Select, Button, message, Spin, Alert, Tooltip, Slider, Switch, Card, Row, Col } from 'antd';
import { DownloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer, Brush, ReferenceArea
} from 'recharts';

import { useData } from '../contexts/DataContext';

const { Option } = Select;

const Config = () => {
  const {
    config, 
    availablePorts, 
    handlePortSelect, 
    isMeasuring,
    startConfigurePort,
    isConfiguring,
    hasPortPermission,
    error,
    serialData,
    requestPortPermissionAndList,
    handleChange,
    chartData,
    handleStop,
    updateChartData,
    clearSerialData,
  } = useData();

  const [isStopping, setIsStopping] = useState(false);
  const [calculatedStartPoint, setCalculatedStartPoint] = useState(0);
  const [calculatedRange, setCalculatedRange] = useState(0);
  const [rangeError, setRangeError] = useState(false);
  const [stepLengthError, setStepLengthError] = useState(false);

  // Kalman filter state for distance only
  const [useKalmanFilter, setUseKalmanFilter] = useState(true);
  const [kalmanQ, setKalmanQ] = useState(0.1); // Process noise covariance
  const [kalmanR, setKalmanR] = useState(1);   // Measurement noise covariance
  const [kalmanState, setKalmanState] = useState(null); // Estimated distance
  const [kalmanCovariance, setKalmanCovariance] = useState(1); // Estimate covariance

  // State to hold processed chart data after applying Kalman filter
  const [processedChartData, setProcessedChartData] = useState([]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    calculateRange();
  }, [config.surface_distance, config.num_points, config.step_length, config.sensor_angle]);


  useEffect(() => {
  
    if (!chartData || chartData.length === 0) {
      console.log("No chart data available");
      setProcessedChartData([]);
      return;
    }
  
    let newProcessedData = chartData.map((item, index) => {
      const time = (index * config.report_interval_ms) / 1000;
      let processedItem = {
        ...item,
        time: parseFloat(time.toFixed(3)),
        distance: item.distance !== undefined && !isNaN(item.distance)
          ? parseFloat(item.distance.toFixed(3))
          : null,
        velocity: item.velocity !== undefined && !isNaN(item.velocity)
          ? parseFloat(item.velocity.toFixed(2))
          : null
      };
  
      if (useKalmanFilter && processedItem.distance !== null) {
        // Apply Kalman filter to distance only if it's enabled and distance is valid
        let currentState = kalmanState !== null ? kalmanState : processedItem.distance;
        let currentCovariance = kalmanCovariance;
  
        // Prediction Step
        const predictedState = currentState;
        const predictedCovariance = currentCovariance + kalmanQ;
  
        // Kalman Gain Calculation
        const kalmanGain = predictedCovariance / (predictedCovariance + kalmanR);
  
        // Update Step
        const updatedState = predictedState + kalmanGain * (processedItem.distance - predictedState);
        const updatedCovariance = (1 - kalmanGain) * predictedCovariance;
  
        // Update processed item with Kalman filtered distance
        processedItem.distance = parseFloat(updatedState.toFixed(3));
  
        // Update Kalman filter state
        setKalmanState(updatedState);
        setKalmanCovariance(updatedCovariance);
      }
  
      return processedItem;
    });
  
    console.log("Processed chart data:", newProcessedData);
    setProcessedChartData(newProcessedData);
  }, [chartData, useKalmanFilter, kalmanQ, kalmanR, config.report_interval_ms, kalmanState, kalmanCovariance]);
  
  // Add this separate effect to log processed data whenever it changes
  useEffect(() => {
    console.log("Current processed chart data:", processedChartData);
  }, [processedChartData]);
  const onPortChange = (selectedValue) => {
    if (!selectedValue) {
      handlePortSelect(null);
      return;
    }
    const [usbVendorId, usbProductId] = selectedValue.split(':').map(Number);
    handlePortSelect({ usbVendorId, usbProductId });
  };

  const handleStopMeasurement = async () => {
    try {
      setIsStopping(true);
      await handleStop();
    } catch (error) {
      console.error('Failed to stop measurement:', error);
      message.error('Failed to stop measurement. Please try again.');
    } finally {
      setIsStopping(false);
    }
  };

  const calculateRange = () => {
    const { surface_distance, num_points, step_length, sensor_angle } = config;
  
    const base_step_length_m = 0.0025;
    const angleRadians = sensor_angle * (Math.PI / 180);
    const optimal_point = surface_distance / Math.cos(angleRadians);
    const start_point_angled = optimal_point - ((num_points - 1) / 2) * step_length * base_step_length_m;
    const start_point = start_point_angled * Math.cos(angleRadians);
    const range_angled = start_point_angled + (num_points - 1) * step_length * base_step_length_m;
    const range = range_angled * Math.cos(angleRadians);
  
    setCalculatedStartPoint(start_point);
    setCalculatedRange(range);
  
    setRangeError(start_point <= 0);
    setStepLengthError(!(24 % step_length === 0 || step_length % 24 === 0));
  };

  const renderSerialData = () => {
    if (Array.isArray(serialData)) {
      return serialData.slice(-100).map((data, index) => (
        <div key={index} className="text-sm mb-1">
          {JSON.stringify(data)}
        </div>
      ));
    } else if (typeof serialData === 'string') {
      return serialData.split('\n').slice(-100).map((line, index) => (
        <div key={index} className="text-sm mb-1">
          {line}
        </div>
      ));
    } else if (serialData && typeof serialData === 'object') {
      return (
        <div className="text-sm mb-1">
          {JSON.stringify(serialData)}
        </div>
      );
    } else {
      return <div>No data available</div>;
    }
  };

  const renderConfigInput = (label, key, step, type = 'number', tooltipText) => (
    <div>
      <Tooltip title={tooltipText}>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          {label}
          <InfoCircleOutlined className="ml-1" />
        </label>
      </Tooltip>
      <input
        type={type}
        value={config[key]}
        onChange={(e) => handleChange(key, type === 'number' ? parseFloat(e.target.value) : e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md"
        step={step}
      />
    </div>
  );

  const renderSlider = (label, key, tooltipText) => (
    <div className="mb-6">
      <Tooltip title={tooltipText}>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
          {label}
          <InfoCircleOutlined className="ml-1" />
        </label>
      </Tooltip>
      <Slider
        min={0}
        max={1}
        step={0.01}
        value={config[key]}
        onChange={(value) => handleChange(key, value)}
      />
      <div className="text-xs text-gray-500 mt-1">
        Value: {config[key]}
      </div>
    </div>
  );

  const renderChart = (dataKeys, yAxisLabel, colors) => {
    const [zoomState, setZoomState] = useState(null);
    const [zoomInProgress, setZoomInProgress] = useState(false);
  
    const displayData = processedChartData.slice(-100);
  
    const minTime = displayData[0]?.time || 0;
    const maxTime = displayData[displayData.length - 1]?.time || 100;
  
    const yValues = displayData
      .flatMap(item => dataKeys.map(key => item[key]))
      .filter(val => !isNaN(val) && val !== null);
    const minY = Math.min(...yValues, 0);
    const maxY = Math.max(...yValues);
    const yDomain = [minY, maxY + (maxY - minY) * 0.1];
  
    const handleZoom = (domain) => {
      setZoomState(domain);
    };
  
    const handleMouseDown = (e) => {
      if (e && e.activeLabel) {
        setZoomInProgress(true);
        setZoomState({ refAreaLeft: e.activeLabel });
      }
    };
  
    const handleMouseMove = (e) => {
      if (zoomInProgress && e && e.activeLabel) {
        setZoomState(prevState => ({
          ...prevState,
          refAreaRight: e.activeLabel
        }));
      }
    };
  
    const handleMouseUp = () => {
      if (zoomInProgress) {
        setZoomInProgress(false);
        if (zoomState.refAreaLeft && zoomState.refAreaRight) {
          if (zoomState.refAreaLeft === zoomState.refAreaRight || zoomState.refAreaRight === undefined) {
            setZoomState(null);
          } else {
            const [left, right] = [zoomState.refAreaLeft, zoomState.refAreaRight].sort((a, b) => a - b);
            handleZoom({ left, right });
          }
        }
      }
    };
  
    const resetZoom = () => {
      setZoomState(null);
    };
  
    const formatTMinus = (time) => {
      const tMinus = maxTime - time;
      return `T${tMinus >= 0 ? '-' : '+'}${Math.abs(tMinus).toFixed(1)}`;
    };
  
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={displayData}
            margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="time" 
              type="number"
              domain={zoomState ? [zoomState.left, zoomState.right] : [minTime, maxTime]}
              tickFormatter={formatTMinus}
              label={{ value: 'T-minus Time (s)', position: 'insideBottomRight', offset: -10 }}
            />
            <YAxis 
              label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }}
              domain={yDomain}
              tickFormatter={(value) => {
                if (yAxisLabel.includes('Distance')) {
                  return value.toFixed(3); // Keep 3 decimal places for distance
                }
                return value.toFixed(2); // Default formatting for other data
              }}
            />
            <RechartsTooltip 
              labelFormatter={formatTMinus}
              formatter={(value, name) => {
                if (name.toLowerCase().includes('distance')) {
                  return [value !== null ? value.toFixed(3) : 'N/A', name];
                }
                return [value !== null ? value.toFixed(2) : 'N/A', name];
              }}
            />
            <Legend />
            {dataKeys.map((key, index) => (
              <Line 
                key={key}
                name={key.charAt(0).toUpperCase() + key.slice(1)} // Capitalize first letter
                type="monotone"
                dataKey={key}
                stroke={colors[index]}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
            <Brush
              dataKey="time"
              height={30}
              stroke={colors[0]}
              startIndex={0}
              endIndex={displayData.length - 1}
              tickFormatter={formatTMinus}
            />
            {zoomInProgress && zoomState && zoomState.refAreaLeft && zoomState.refAreaRight && (
              <ReferenceArea
                x1={zoomState.refAreaLeft}
                x2={zoomState.refAreaRight}
                strokeOpacity={0.3}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
        {zoomState && (
          <button 
            style={{ position: 'absolute', right: 10, top: 10 }}
            onClick={resetZoom}
          >
            Reset Zoom
          </button>
        )}
      </div>
    );
  };

  const exportChartData = () => {
    const csvContent = [
      ['Time (s)', 'Distance (m)', 'Velocity (m/s)'],
      ...processedChartData.map(item => [
        item.time !== null ? item.time.toFixed(3) : '',
        item.distance !== undefined && item.distance !== null ? item.distance.toFixed(3) : '',
        item.velocity !== undefined && item.velocity !== null ? item.velocity.toFixed(2) : ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'chart_data.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderKalmanFilterConfig = () => (
    <Card title="Kalman Filter Configuration" className="mb-6">
      <Row gutter={16}>
        <Col span={12}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Kalman Filter
          </label>
          <Switch
            checked={useKalmanFilter}
            onChange={(checked) => {
              setUseKalmanFilter(checked);
              setKalmanState(null); // Reset Kalman filter state when toggled
              setKalmanCovariance(1); // Reset covariance
            }}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
          <div className="text-xs text-gray-500 mt-1">
            {useKalmanFilter ? 'ON' : 'OFF'}
          </div>
        </Col>
      </Row>

      {useKalmanFilter && (
        <>
          <Row gutter={16} className="mt-4">
            <Col span={12}>
              <Tooltip title="Higher values make the filter more responsive to new measurements">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Process Noise Covariance (Q)
                  <InfoCircleOutlined className="ml-1" />
                </label>
              </Tooltip>
              <input
                type="number"
                value={kalmanQ}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setKalmanQ(isNaN(value) ? kalmanQ : value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                step="0.01"
                min="0"
              />
            </Col>
            <Col span={12}>
              <Tooltip title="Higher values make the filter more resistant to measurement noise">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Measurement Noise Covariance (R)
                  <InfoCircleOutlined className="ml-1" />
                </label>
              </Tooltip>
              <input
                type="number"
                value={kalmanR}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  setKalmanR(isNaN(value) ? kalmanR : value);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                step="0.1"
                min="0"
              />
            </Col>
          </Row>
        </>
      )}
    </Card>
  );

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Configuration Panel */}
      <div className="w-1/3 p-6 bg-white shadow-lg overflow-y-auto">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Sensor Configuration</h2>
        
        {/* Port Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Port</label>
          <Select
            value={config.port ? `${config.port.usbVendorId}:${config.port.usbProductId}` : undefined}
            onChange={onPortChange}
            className="w-full mb-4"
            loading={isConfiguring}
            disabled={isConfiguring || isMeasuring}
            placeholder="Select a port"
          >
            {availablePorts.map(port => (
              <Option key={`${port.usbVendorId}:${port.usbProductId}`} value={`${port.usbVendorId}:${port.usbProductId}`}>
                {`Port ${port.usbVendorId}:${port.usbProductId}`}
              </Option>
            ))}
          </Select>
          <div className="flex justify-between">
            <Button
              onClick={requestPortPermissionAndList}
              className="bg-blue-500 text-white"
              disabled={hasPortPermission || isConfiguring || isMeasuring}
            >
              Request Port Access
            </Button>
          </div>
        </div>

        {/* Sensor Setup */}
        <Card title="Sensor Setup" className="mb-6">
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Surface Distance (m)",
                "surface_distance",
                "0.01",
                "number",
                "Vertical distance from the sensor to the surface being measured."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "Sensor Angle (°)",
                "sensor_angle",
                "0.1",
                "number",
                "Angle of the sensor relative to the surface."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Report Interval (ms)",
                "report_interval_ms",
                "100",
                "number",
                "The interval at which the sensor reports measurements, in milliseconds."
              )}
            </Col>
          </Row>
        </Card>

        {/* Measurement Configuration */}
        <Card title="Measurement Configuration" className="mb-6">
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Sweep Rate (Hz)",
                "sweep_rate",
                "1",
                "number",
                "Rate of sensor sweeps. Higher rates improve temporal accuracy for fast-moving objects."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "HWAAS",
                "hwaas",
                "1",
                "number",
                "Hardware Accelerated Averages. Higher values increase accuracy by reducing random noise, but may decrease accuracy for rapidly changing measurements."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Num Points",
                "num_points",
                "1",
                "number",
                "Number of measurement points. More points increase spatial accuracy and resolution but extend measurement time."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "Step Length",
                "step_length",
                "1",
                "number",
                "Length between measurement points. Must be a divisor or multiplier of 24."
              )}
            </Col>
          </Row>
        </Card>

        {/* Filtering and Processing */}
        <Card title="Filtering and Processing" className="mb-6">
          <Row gutter={16}>
            <Col span={24}>
              {renderSlider(
                "PSD Low-pass Coefficient",
                "psd_lp_coeff",
                "Smoothing for Power Spectral Density. Higher values increase stability and reduce noise in frequency domain."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              {renderSlider(
                "Velocity Low-pass Coefficient",
                "velocity_lp_coeff",
                "Smoothing for velocity data. Higher values increase accuracy for steady velocities but reduce accuracy for rapidly changing velocities."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              {renderSlider(
                "Threshold Sensitivity",
                "threshold_sensitivity",
                "Sensitivity for valid signal detection. Higher values may improve accuracy for weak signals but could introduce false positives in noisy environments."
              )}
            </Col>
          </Row>
        </Card>

        {/* Kalman Filter Configuration */}
        {renderKalmanFilterConfig()}

        {/* Advanced Settings */}
        <Card title="Advanced Settings">
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "CFAR Guard",
                "cfar_guard",
                "1",
                "number",
                "Size of the guard region around a peak when using the CFAR algorithm."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "CFAR Win",
                "cfar_win",
                "1",
                "number",
                "Size of the window used by the CFAR algorithm to detect peaks."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Max Peak Interval (s)",
                "max_peak_interval_s",
                "0.1",
                "number",
                "Maximum time interval between two peaks for them to be considered part of the same motion event."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "Time Series Length",
                "time_series_length",
                "1",
                "number",
                "The length of the time series used for processing the signal."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              {renderConfigInput(
                "Slow Zone Half Length",
                "slow_zone_half_length",
                "1",
                "number",
                "The half-length of the 'slow zone' where velocity changes are expected to be small."
              )}
            </Col>
            <Col span={12}>
              {renderConfigInput(
                "Sweeps Per Frame",
                "sweeps_per_frame",
                "1",
                "number",
                "The number of sweeps per frame for measurement."
              )}
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Tooltip title="Whether the sensor runs in continuous sweep mode.">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Continuous Sweep Mode
                  <InfoCircleOutlined className="ml-1" />
                </label>
              </Tooltip>
              <Switch
                checked={config.continuous_sweep_mode}
                onChange={(checked) => handleChange('continuous_sweep_mode', checked)}
              />
            </Col>
            <Col span={12}>
              <Tooltip title="Whether to use double buffering for processing data.">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  Double Buffering
                  <InfoCircleOutlined className="ml-1" />
                </label>
              </Tooltip>
              <Switch
                checked={config.double_buffering}
                onChange={(checked) => handleChange('double_buffering', checked)}
              />
            </Col>
          </Row>
        </Card>

        {/* Detection Range and Errors */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Detection Range: {calculatedStartPoint.toFixed(3)} - {calculatedRange.toFixed(3)} meters
          </h3>
          
          {rangeError && (
            <Alert 
              message="Start point must be greater than zero"
              type="error"
              showIcon
            />
          )}

          {stepLengthError && (
            <Alert 
              message="Step length must be a divisor or multiplier of 24"
              type="error"
              showIcon
            />
          )}
        </div>

        {/* Control Buttons */}
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={startConfigurePort}
            className="bg-green-500 text-white"
            disabled={!config.port || isMeasuring || isConfiguring}
          >
            Start
          </Button>
          <Button
            onClick={handleStopMeasurement}
            className="bg-red-500 text-white"
            disabled={!isMeasuring || isStopping}
          >
            Stop
          </Button>
          {(isStopping || isConfiguring || isMeasuring) && <Spin />}
        </div>

        {/* Serial Data Display */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-800">Serial Data</h3>
          <div className="bg-gray-100 p-4 rounded-md h-40 overflow-y-auto">
            {renderSerialData()}
          </div>
          <Button onClick={clearSerialData} className="mt-4 bg-gray-500 text-white">
            Clear Serial Data
          </Button>
        </div>
      </div>

      {/* Chart Panel */}
      <div className="w-2/3 p-6 flex flex-col h-full">
        <div className="flex justify-end space-x-4 mb-4">
          <Button
            icon={<DownloadOutlined />}
            onClick={exportChartData}
            className="flex items-center"
          >
            <span className="ml-2">Export Data</span>
          </Button>
        </div>
        <div className="flex-grow flex flex-col" style={{ minHeight: '600px' }}>
          {/* Distance Chart */}
          <div style={{ height: '45%', minHeight: '300px', overflow: 'visible' }}>
            {renderChart(['distance'], 'Distance (m)', ['#82ca9d'])}
          </div>
          {/* Velocity Chart */}
          <div style={{ height: '45%', minHeight: '300px', overflow: 'visible' }}>
            {renderChart(['velocity'], 'Velocity (m/s)', ['#8884d8'])}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Config;
